import { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';

interface Transformation {
  id: number;
  title: string;
  procedure: string;
  description: string;
  hasRealImage?: boolean;
  imageSrc?: string;
  beforeGradient: string;
  afterGradient: string;
  beforeLabel: string;
  afterLabel: string;
}

const transformations: Transformation[] = [
  {
    id: 1,
    title: 'Smile Makeover',
    procedure: 'Full Restoration Treatment',
    description: 'Complete transformation — aligned, whitened, and restored for a confident, radiant smile.',
    hasRealImage: true,
    imageSrc: '/before-after-smile.png',
    beforeGradient: 'linear-gradient(135deg, #d4a574 0%, #c9956a 30%, #bf8560 60%, #b57555 100%)',
    afterGradient: 'linear-gradient(135deg, #f5f0eb 0%, #ebe5df 30%, #ffffff 60%, #f8f5f0 100%)',
    beforeLabel: 'Before',
    afterLabel: 'After',
  },
  {
    id: 2,
    title: 'Teeth Whitening',
    procedure: 'Professional Whitening Treatment',
    description: 'Dramatically brighter smile in just one visit with our advanced whitening system.',
    beforeGradient: 'linear-gradient(135deg, #c4a882 0%, #b89b72 30%, #a88e65 60%, #9a8158 100%)',
    afterGradient: 'linear-gradient(135deg, #f0f5f3 0%, #e8f0ed 30%, #ffffff 60%, #f5faf8 100%)',
    beforeLabel: 'Before',
    afterLabel: 'After Whitening',
  },
  {
    id: 3,
    title: 'Dental Veneers',
    procedure: 'Porcelain Veneer Transformation',
    description: 'Complete smile makeover with custom-crafted porcelain veneers for a natural, stunning result.',
    beforeGradient: 'linear-gradient(135deg, #d4a574 0%, #c29266 25%, #bf8560 50%, #a87550 100%)',
    afterGradient: 'linear-gradient(135deg, #f2f0ed 0%, #e8e5e0 30%, #ffffff 60%, #f5f2ee 100%)',
    beforeLabel: 'Before',
    afterLabel: 'After Veneers',
  },
  {
    id: 4,
    title: 'Invisalign',
    procedure: 'Clear Aligner Treatment',
    description: 'Straighten your teeth discreetly with custom clear aligners — no metal brackets needed.',
    beforeGradient: 'linear-gradient(135deg, #bf8560 0%, #b07a55 30%, #a06e4a 60%, #906340 100%)',
    afterGradient: 'linear-gradient(135deg, #f8f5f0 0%, #f0ece5 30%, #ffffff 60%, #faf8f5 100%)',
    beforeLabel: 'Before',
    afterLabel: 'After Invisalign',
  },
];

function TransformationSlider({ transformation }: { transformation: Transformation }) {
  const [sliderPos, setSliderPos] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const handleMove = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPos(percentage);
  }, []);

  const handleMouseDown = () => { isDragging.current = true; };
  const handleMouseUp = () => { isDragging.current = false; };
  const handleMouseMove = (e: React.MouseEvent) => { if (isDragging.current) handleMove(e.clientX); };
  const handleTouchMove = (e: React.TouchEvent) => { handleMove(e.touches[0].clientX); };

  const renderContent = (side: 'before' | 'after') => {
    if (transformation.hasRealImage && transformation.imageSrc) {
      return (
        <img
          src={transformation.imageSrc}
          alt={`${side} dental transformation`}
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            objectPosition: side === 'before' ? 'left center' : 'right center',
          }}
          draggable={false}
        />
      );
    }
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-4 ${
          side === 'before' ? 'bg-white/20' : 'bg-white/30'
        }`}>
          <svg className={`w-12 h-12 ${side === 'before' ? 'text-white/60' : 'text-[var(--color-primary)]'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M12 2C8.5 2 6 4.5 6 7c0 1.5.5 2.8 1.3 3.8C6.5 12 6 13.5 6 15c0 3.5 2.5 7 6 7s6-3.5 6-7c0-1.5-.5-3-1.3-4.2C17.5 9.8 18 8.5 18 7c0-2.5-2.5-5-6-5z" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="flex gap-1 mb-3">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className={`w-3 h-5 rounded-sm ${
              side === 'before' ? 'bg-white/30' : 'bg-white/70 shadow-sm'
            } ${side === 'before' && i === 3 ? 'rotate-6' : ''} ${side === 'before' && i === 4 ? '-rotate-3' : ''}`} />
          ))}
        </div>
        <p className={`text-sm font-semibold uppercase tracking-widest ${
          side === 'before' ? 'text-white/70' : 'text-[var(--color-primary-dark)]/60'
        }`}>{side === 'before' ? transformation.beforeLabel : transformation.afterLabel}</p>
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden cursor-ew-resize select-none shadow-xl group"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onMouseMove={handleMouseMove}
      onTouchMove={handleTouchMove}
      onTouchStart={handleMouseDown}
      onTouchEnd={handleMouseUp}
    >
      {/* After (full background) */}
      <div
        className="absolute inset-0"
        style={{ background: transformation.afterGradient }}
      >
        {renderContent('after')}
      </div>

      {/* Before (clipped) */}
      <div
        className="absolute inset-0"
        style={{
          background: transformation.beforeGradient,
          clipPath: `inset(0 ${100 - sliderPos}% 0 0)`,
        }}
      >
        {renderContent('before')}
      </div>

      {/* Slider handle */}
      <div
        className="absolute top-0 bottom-0 z-20"
        style={{ left: `${sliderPos}%`, transform: 'translateX(-50%)' }}
      >
        <div className="w-0.5 h-full bg-white shadow-lg" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
          <div className="flex items-center gap-0.5">
            <svg className="w-4 h-4 text-[var(--color-primary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            <svg className="w-4 h-4 text-[var(--color-primary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Labels */}
      <div className="absolute top-4 left-4 z-10 px-3 py-1.5 rounded-full bg-[var(--color-navy)]/60 backdrop-blur-sm text-white text-xs font-semibold uppercase tracking-wider">
        {transformation.beforeLabel}
      </div>
      <div className="absolute top-4 right-4 z-10 px-3 py-1.5 rounded-full bg-[var(--color-primary)]/60 backdrop-blur-sm text-white text-xs font-semibold uppercase tracking-wider">
        {transformation.afterLabel}
      </div>
    </div>
  );
}

export default function SmileGallery() {
  const [activeIdx, setActiveIdx] = useState(0);

  return (
    <section id="transformations" className="section-padding relative">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[var(--color-primary)]/[0.02] to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--color-primary)]/10 text-[var(--color-primary)] text-sm font-medium mb-6">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Smile Transformations
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: 'var(--font-display)' }}>
            See the <span className="gradient-text">Difference</span> We Make
          </h2>
          <p className="text-lg text-[var(--color-text-light)] leading-relaxed">
            Drag the slider to reveal incredible smile transformations achieved by Dr. Abis-Velasco's skilled hands.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Main Slider */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-3"
          >
            <TransformationSlider transformation={transformations[activeIdx]} />
          </motion.div>

          {/* Sidebar cards */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="lg:col-span-2 flex flex-col gap-3"
          >
            {transformations.map((t, i) => (
              <button
                key={t.id}
                onClick={() => setActiveIdx(i)}
                className={`w-full text-left p-5 rounded-2xl transition-all duration-400 ${
                  activeIdx === i
                    ? 'bg-white shadow-xl shadow-[var(--color-primary)]/10 border-2 border-[var(--color-primary)]/20'
                    : 'bg-white/50 hover:bg-white/80 border-2 border-transparent hover:border-[var(--color-primary)]/10'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${
                    activeIdx === i
                      ? 'bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-primary-light)] text-white'
                      : 'bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                  }`}>
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                      {i === 0 && <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />}
                      {i === 1 && <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />}
                      {i === 2 && <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />}
                      {i === 3 && <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />}
                    </svg>
                  </div>
                  <div>
                    <h3 className={`font-semibold text-base mb-1 transition-colors ${activeIdx === i ? 'text-[var(--color-navy)]' : 'text-[var(--color-text)]'}`}>
                      {t.title}
                    </h3>
                    <p className="text-xs text-[var(--color-primary)] font-medium mb-1">{t.procedure}</p>
                    <p className="text-sm text-[var(--color-text-light)] leading-relaxed">{t.description}</p>
                  </div>
                </div>
              </button>
            ))}

            <button
              onClick={() => document.getElementById('appointment')?.scrollIntoView({ behavior: 'smooth' })}
              className="mt-2 w-full py-4 px-6 bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-primary-light)] text-white font-semibold rounded-2xl shadow-lg shadow-[var(--color-primary)]/20 hover:shadow-[var(--color-primary)]/35 hover:scale-[1.02] transition-all duration-300 text-center"
            >
              Book Your Transformation →
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
